import { type FC, useEffect, useRef } from 'react';

import { useRouter } from 'next/router';

/**
 * Writes the previousPageURL and currentPageURL to the history API.
 * @param previousPageURL - URL of the previous page.
 * @param currentPageURL - URL of the current page.
 */
const setBrowserHistoryBack = (previousPageURL: string | null, currentPageURL: string | null): void => {
	if (
		(!window.history.state || (window.history.state && !window.history.state.currentPage)) &&
		previousPageURL &&
		currentPageURL
	) {
		history.replaceState(
			{
				previousPage: !0,
			},
			'',
			previousPageURL,
		);
		history.pushState(
			{
				currentPage: !0,
			},
			'',
			currentPageURL,
		);

		// Fix to actually navigate to the page when a user clicks the back button.
		addEventListener('popstate', function (e) {
			e.state && e.state.previousPage && window.location.replace(previousPageURL);
		});
	}
};

/**
 * This component pushes the previous and current page URL to the history API.
 * Please note that the history is only being set in Chrome after interacting with the newly opened page: https://stackoverflow.com/questions/58128939/changing-window-location-hash-creates-entry-in-history-but-doesnt-affect-chrome
 * @returns {ReactElement} The element containing the previous and current url's.
 */
export const BrowserHistory: FC = () => {
	const router = useRouter();
	const browserHistoryRoot = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const hash = window.location.hash;
		const referUrl = new URLSearchParams(hash.replace('#', '')).get('referURL');

		if (referUrl && browserHistoryRoot?.current) {
			setBrowserHistoryBack(referUrl, `${browserHistoryRoot.current.getAttribute('data-current-url')}${hash}`);
		}
	}, []);

	return <div ref={browserHistoryRoot} data-current-url={router.asPath.split('#')[0]} />;
};
